import React, {
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import classNames from 'classnames';
import { BaseTextFieldProps } from '@mui/material/TextField/TextField';
import { MenuProps, useStyles } from './AppSelectWrapper.styles';
import theme from '../../../styles/theme';
import { ReactComponent } from 'tss-react/tools/ReactComponent';
const containsText = (text: string, searchText: string) =>
  text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1;

interface IAppSelectWrapper extends BaseTextFieldProps {
  additionalOptions?: Array<any>;
  defaultValue?: any;
  options?: Array<any>;
  explainingText?: string;
  disableSorting?: boolean;
  disableClear?: boolean;
  sortBy?: string;
  autoWidth?: boolean;
  label?: string;
  name?: string;
  onChange?: (value?: any) => void;
  onClearCallback?: () => void;
  error?: boolean;
  helperText?: string;
  viewMode?: boolean;
  fieldName?: string;
  itemWrapperCallback?: (...props: any[]) => ReactComponent<any> | ReactElement;
}

const AppSelectWrapper: React.FC<IAppSelectWrapper> = ({
  disabled = false,
  name,
  className = '',
  additionalOptions = [],
  options = [],
  label,
  defaultValue,
  explainingText = undefined,
  disableSorting = false,
  disableClear = false,
  fieldName = 'name',
  sortBy = fieldName,
  autoWidth = true,
  error = false,
  itemWrapperCallback,
  onChange = (value?: any) => {},
  onClearCallback = () => {},
  helperText,
  viewMode = false,
  ...props
}) => {
  const { classes } = useStyles(theme);
  const [searchText, setSearchText] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  const index = useMemo(
    () =>
      [...additionalOptions, ...options]?.findIndex((option) => {
        const optionId = option?.id;

        return optionId === defaultValue || optionId === defaultValue?.id;
      }),
    [options, additionalOptions, defaultValue],
  );

  const sortedOptions = useMemo(() => {
    const mainOptions = !disableSorting
      ? (options ? [...options] : [])?.sort((a, b) =>
          `${a[sortBy]}`.localeCompare(`${b[sortBy]}`),
        )
      : options;
    return [...additionalOptions, ...mainOptions];
  }, [options, additionalOptions, disableSorting, sortBy]);

  const valueOutOfOption = useMemo(() => {
    const hasDefaultValue = index === -1 && defaultValue?.[fieldName];

    if (hasDefaultValue) {
      return {
        [fieldName]: defaultValue[fieldName],
        disabled: true,
      };
    }

    return null;
  }, [index, defaultValue]);

  const value = useMemo(
    () =>
      index === -1 || index === undefined
        ? valueOutOfOption || ''
        : [...additionalOptions, ...options][index],
    [index, valueOutOfOption, options, additionalOptions],
  );

  const renderOptions = useCallback(() => {
    const renderItems = valueOutOfOption
      ? [valueOutOfOption, ...(sortedOptions || [])]
      : sortedOptions;

    return renderItems?.map((item, index) => (
      <MenuItem
        className={classNames({
          [classes.hidden]:
            !containsText(item?.[fieldName], searchText) || item.disabled,
        })}
        key={`${item.id}${item[fieldName]}${index}`}
        value={item}
        id={item.id}>
        {itemWrapperCallback ? itemWrapperCallback(item) : item[fieldName]}
      </MenuItem>
    ));
  }, [valueOutOfOption, sortedOptions, searchText]);

  if (!(options.length + additionalOptions.length)) {
    return (
      <TextField
        fullWidth
        disabled={true}
        label={label}
        className={classes.formControl}
        value={defaultValue?.[fieldName]}
        name={name}
        margin="dense"
        variant="outlined"
        {...props}
      />
    );
  }

  if (viewMode)
    return (
      <Box className={classes.viewModeWrapper}>
        {label && <Typography variant="text-md-sb">{label}</Typography>}
        <Typography variant="text-md">{defaultValue?.name}</Typography>
      </Box>
    );

  return (
    <FormControl className={classes.formControl}>
      <TextField
        fullWidth
        select
        label={label}
        disabled={disabled}
        id={name}
        name={name}
        ref={ref}
        margin="dense"
        variant="outlined"
        value={value}
        onChange={(d: React.ChangeEvent<HTMLInputElement>) => {
          onChange(d);
        }}
        error={error}
        helperText={helperText}
        className={
          !!explainingText && !defaultValue
            ? classNames(classes.root, classes.labelWithExplaining, className)
            : classNames(className, classes.root)
        }
        SelectProps={{
          autoWidth,
          MenuProps: {
            ...MenuProps,
            PaperProps: {
              sx: {
                width: ref?.current?.offsetWidth,
                maxWidth: `${ref?.current?.offsetWidth} !important`,
                ...MenuProps.PaperProps.sx,
              },
            },
          },
          onClose: () => {
            setTimeout(() => (document.activeElement as HTMLElement)?.blur());
          },
        }}
        InputLabelProps={{
          shrink: !!value,
        }}
        InputProps={{
          endAdornment:
            !disabled && defaultValue && !disableClear ? (
              <InputAdornment
                position="end"
                style={{ right: 35, position: 'absolute' }}>
                <IconButton
                  disabled={disabled}
                  className={classes.closeIcon}
                  aria-label="remove value"
                  onClick={() => {
                    onChange();
                  }}
                  size="large">
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          startAdornment: explainingText ? (
            <InputAdornment position="start">
              <Tooltip
                title={explainingText}
                classes={{ popper: classes.toolTip }}>
                <IconButton size="large" className={classes.explainingToolTip}>
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : null,
        }}
        {...props}>
        <ListSubheader
          style={{
            paddingTop: 8,
            paddingRight: 16,
          }}>
          <TextField
            onClick={(e) => e.stopPropagation()}
            size={'small'}
            style={{
              backgroundColor: 'white',
            }}
            autoFocus={true}
            variant={'outlined'}
            placeholder="Type to search..."
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onFocus={() => {
              setSearchText('');
            }}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
          />
        </ListSubheader>
        {renderOptions()}
      </TextField>
    </FormControl>
  );
};

export default AppSelectWrapper;
